import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  Building2,
  LayoutDashboard,
  Package,
  Truck,
  Phone,
  Users,
  UsersRound,
  Calendar,
  TreePine,
  BarChart3,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Euro,
  Shield,
  UserCog,
  Wrench
} from 'lucide-react';

const Sidebar = ({ user, onLogout }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { canAccess, isAdminOrManager, isTechniker } = usePermissions();

  const navigation = [
    {
      name: 'Dashboard',
      path: '/',
      icon: LayoutDashboard,
      emoji: '🏠',
      module: 'dashboard',
      roles: ['admin', 'manager'] // Techniker hat KEINEN Zugriff
    },
    {
      name: 'Lagerverwaltung',
      path: '/inventory',
      icon: Package,
      emoji: '📦',
      module: 'inventory',
      roles: ['admin', 'manager', 'techniker']
    },
    {
      name: 'Fuhrpark',
      path: '/fleet',
      icon: Truck,
      emoji: '🚐',
      module: 'fleet',
      roles: ['admin', 'manager']
    },
    {
      name: 'Telekommunikation',
      path: '/telecommunications',
      icon: Phone,
      emoji: '📱',
      module: 'telecommunications',
      roles: ['admin', 'manager']
    },
    {
      name: 'Mitarbeiter',
      path: '/employees',
      icon: Users,
      emoji: '👷',
      module: 'employees',
      roles: ['admin', 'manager'] // Techniker hat KEINEN Zugriff
    },
    {
      name: 'Teams',
      path: '/teams',
      icon: UsersRound,
      emoji: '👥',
      module: 'teams',
      roles: ['admin', 'manager'] // Techniker hat KEINEN Zugriff
    },
    {
      name: 'Kalender',
      path: '/calendar',
      icon: Calendar,
      emoji: '📅',
      module: 'calendar',
      roles: ['admin', 'manager', 'techniker']
    },
    {
      name: 'Abrechnung',
      path: '/billing-overview',
      icon: Euro,
      emoji: '💶',
      module: 'billing',
      roles: ['admin'] // NUR Admin, Manager hat KEINEN Zugriff
    },
    {
      name: 'Meine Abrechnungen',
      path: '/my-billings',
      icon: Euro,
      emoji: '💶',
      module: 'billing',
      roles: ['techniker'] // Nur Techniker
    },
    {
      name: 'Urlaub',
      path: '/vacation',
      icon: TreePine,
      emoji: '🌴',
      module: 'vacation',
      roles: ['admin', 'manager', 'techniker']
    },
    {
      name: 'Analyse',
      path: '/analysis',
      icon: BarChart3,
      emoji: '📊',
      module: 'analytics',
      roles: ['admin', 'manager'] // Manager hat Zugriff, Techniker NICHT
    },
    {
      name: 'Einstellungen',
      path: '/settings',
      icon: Settings,
      emoji: '⚙️',
      module: 'settings',
      roles: ['admin'] // NUR Admin
    }
  ];

  // Get user role for display
  const getUserRole = () => {
    if (user?.user_type === 'admin') return 'admin';
    if (user?.role) return user.role;
    return 'techniker';
  };
  
  const userRole = getUserRole();
  
  // Get role display info
  const getRoleInfo = () => {
    switch(userRole) {
      case 'admin':
        return { 
          label: 'Administrator', 
          icon: Shield, 
          color: 'from-red-500 to-red-600',
          bgColor: 'bg-red-50',
          textColor: 'text-red-700',
          borderColor: 'border-red-300'
        };
      case 'manager':
        return { 
          label: 'Manager', 
          icon: UserCog, 
          color: 'from-blue-500 to-blue-600',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-300'
        };
      default:
        return { 
          label: 'Techniker', 
          icon: Wrench, 
          color: 'from-green-500 to-green-600',
          bgColor: 'bg-green-50',
          textColor: 'text-green-700',
          borderColor: 'border-green-300'
        };
    }
  };
  
  const roleInfo = getRoleInfo();
  const RoleIcon = roleInfo.icon;
  
  // Filter navigation based on role and permissions
  const allowedNavigation = navigation.filter(item => {
    // Check if role is allowed for this item
    if (item.roles && !item.roles.includes(userRole)) {
      return false;
    }
    
    // Check module permission
    return canAccess(item.module);
  });

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <div data-testid="sidebar" className={`fixed left-0 top-0 h-full bg-white border-r border-slate-200 shadow-lg transition-all duration-300 z-50 ${
      collapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="p-6 border-b border-slate-100">
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'space-x-3'}`}>
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="text-lg font-bold text-slate-900">SKALARTECH</h1>
                <p className="text-xs text-slate-500">Management System</p>
              </div>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 hover:bg-slate-100"
            data-testid="sidebar-toggle-button"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 flex-1">
        <div className="space-y-2">
          {allowedNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <Link key={item.path} to={item.path}>
                <div
                  className={`
                    group flex items-center px-3 py-3 rounded-xl transition-all duration-200
                    ${active 
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-lg' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }
                    ${collapsed ? 'justify-center' : 'space-x-3'}
                  `}
                  data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
                >
                  <div className="flex items-center justify-center w-6 h-6">
                    {collapsed ? (
                      <span className="text-lg">{item.emoji}</span>
                    ) : (
                      <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-slate-500 group-hover:text-slate-700'}`} />
                    )}
                  </div>
                  
                  {!collapsed && (
                    <>
                      <span className="font-medium text-sm flex-1">{item.name}</span>
                      <span className="text-lg opacity-70">{item.emoji}</span>
                    </>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-100">
        {!collapsed && (
          <div className="mb-4">
            {/* User Card */}
            <div className="p-3 bg-slate-50 rounded-lg mb-2">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 bg-gradient-to-br ${roleInfo.color} rounded-full flex items-center justify-center`}>
                  <span className="text-white text-sm font-semibold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {user?.username}
                  </p>
                  <p className="text-xs text-slate-500">Angemeldet</p>
                </div>
              </div>
            </div>
            
            {/* Role Badge */}
            <div className={`flex items-center gap-2 px-3 py-2 ${roleInfo.bgColor} rounded-lg border ${roleInfo.borderColor}`}>
              <RoleIcon className={`w-4 h-4 ${roleInfo.textColor}`} />
              <span className={`text-xs font-semibold ${roleInfo.textColor}`}>
                {roleInfo.label}
              </span>
            </div>
          </div>
        )}
        
        <Button
          variant="ghost"
          onClick={onLogout}
          className={`w-full text-slate-600 hover:text-red-600 hover:bg-red-50 transition-colors ${
            collapsed ? 'px-0 justify-center' : 'justify-start'
          }`}
          data-testid="logout-button"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-3">Abmelden</span>}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;